import { useState, useEffect, useRef, useCallback } from 'react';
import { Nfc, Ban, Loader2, AlertCircle, CheckCircle2, Camera } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { cn } from '@/app/components/ui/utils';
import { isNfcSupported } from '@/hooks/use-nfc';

export interface NfcScannerProps {
  onScan: (result: string) => void;
  onError?: (error: string) => void;
  onClose?: () => void;
  onSwitchToCamera?: () => void;
  className?: string;
}

type ScannerState =
  | 'checking'
  | 'ready'
  | 'scanning'
  | 'success'
  | 'error'
  | 'not_supported'
  | 'permission_denied';

interface NDEFRecord {
  recordType: string;
  mediaType?: string;
  id?: string;
  data?: DataView;
  encoding?: string;
  lang?: string;
  toRecords?: () => NDEFRecord[];
}

interface NDEFMessage {
  records: NDEFRecord[];
}

interface NDEFReadingEvent extends Event {
  serialNumber: string;
  message: NDEFMessage;
}

interface NDEFReader {
  scan(options?: { signal?: AbortSignal }): Promise<void>;
  addEventListener(
    type: 'reading',
    listener: (event: NDEFReadingEvent) => void
  ): void;
  addEventListener(type: 'readingerror', listener: (event: Event) => void): void;
}

declare global {
  interface Window {
    NDEFReader: new () => NDEFReader;
  }
}

function decodeNdefTextRecord(record: NDEFRecord): string | null {
  if (!record.data) return null;

  try {
    const dataView = record.data;
    const buffer = new Uint8Array(
      dataView.buffer,
      dataView.byteOffset,
      dataView.byteLength
    );

    if (buffer.length === 0) return null;

    const statusByte = buffer[0];
    const languageCodeLength = statusByte & 0x3f;

    if (buffer.length <= 1 + languageCodeLength) return null;

    const textBytes = buffer.slice(1 + languageCodeLength);
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(textBytes);
  } catch {
    return null;
  }
}

function extractSerialFromNdefMessage(message: NDEFMessage): string | null {
  for (const record of message.records) {
    if (record.recordType === 'text') {
      const text = decodeNdefTextRecord(record);
      if (text && text.trim()) {
        return text.trim();
      }
    }

    if (record.data) {
      try {
        const dataView = record.data;
        const buffer = new Uint8Array(
          dataView.buffer,
          dataView.byteOffset,
          dataView.byteLength
        );
        const decoder = new TextDecoder('utf-8');
        const text = decoder.decode(buffer);
        if (text && text.trim() && /^[\x20-\x7E]+$/.test(text.trim())) {
          return text.trim();
        }
      } catch {
        // Continue to next record
      }
    }
  }

  return null;
}

export function NfcScanner({
  onScan,
  onError,
  onClose,
  onSwitchToCamera,
  className,
}: NfcScannerProps) {
  const [scannerState, setScannerState] = useState<ScannerState>('checking');
  const [statusMessage, setStatusMessage] = useState('Checking NFC availability...');
  const [scannedValue, setScannedValue] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasScannedRef = useRef(false);

  const startScanning = useCallback(async () => {
    if (!isNfcSupported()) {
      setScannerState('not_supported');
      setStatusMessage(
        'NFC is not supported on this browser. Web NFC only works on Chrome for Android.'
      );
      onError?.('NFC not supported');
      return;
    }

    try {
      setScannerState('scanning');
      setStatusMessage('Hold NFC tag near back of phone...');

      const ndef = new window.NDEFReader();
      abortControllerRef.current = new AbortController();

      ndef.addEventListener('reading', ((event: NDEFReadingEvent) => {
        if (hasScannedRef.current) return;

        let serialNumber: string | null = null;

        if (event.message && event.message.records.length > 0) {
          serialNumber = extractSerialFromNdefMessage(event.message);
        }

        if (!serialNumber && event.serialNumber) {
          serialNumber = event.serialNumber.replace(/:/g, '').toUpperCase();
        }

        if (serialNumber) {
          hasScannedRef.current = true;
          setScannedValue(serialNumber);
          setScannerState('success');
          setStatusMessage(`Success! Serial: ${serialNumber}`);

          setTimeout(() => {
            onScan(serialNumber!);
          }, 800);
        } else {
          setScannerState('error');
          setStatusMessage('Could not extract serial number from tag');
          onError?.('Could not extract serial number from tag');
        }
      }) as EventListener);

      ndef.addEventListener('readingerror', (() => {
        if (hasScannedRef.current) return;
        setScannerState('error');
        setStatusMessage('Error reading NFC tag. Try again.');
        onError?.('Error reading NFC tag');
      }) as EventListener);

      await ndef.scan({ signal: abortControllerRef.current.signal });
      setScannerState('ready');
      setStatusMessage('Hold NFC tag near back of phone...');
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return;
        }

        if (
          error.name === 'NotAllowedError' ||
          error.message.includes('permission')
        ) {
          setScannerState('permission_denied');
          setStatusMessage(
            'NFC permission was denied. Please allow NFC access in your browser settings.'
          );
          onError?.('NFC permission denied');
          return;
        }

        if (error.name === 'NotSupportedError') {
          setScannerState('not_supported');
          setStatusMessage(
            'NFC is not supported on this device or browser.'
          );
          onError?.('NFC not supported');
          return;
        }
      }

      setScannerState('error');
      setStatusMessage(
        error instanceof Error ? error.message : 'Failed to start NFC scanning'
      );
      onError?.(
        error instanceof Error ? error.message : 'Failed to start NFC scanning'
      );
    }
  }, [onScan, onError]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    startScanning();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [startScanning]);

  const handleRetry = () => {
    hasScannedRef.current = false;
    setScannedValue(null);
    setScannerState('checking');
    setStatusMessage('Checking NFC availability...');
    startScanning();
  };

  return (
    <div
      className={cn(
        'relative bg-gray-900 rounded-lg overflow-hidden aspect-square flex flex-col items-center justify-center p-6',
        className
      )}
    >
      {/* Scanning state */}
      {(scannerState === 'checking' ||
        scannerState === 'ready' ||
        scannerState === 'scanning') && (
        <>
          <div className="relative">
            <Nfc className="h-24 w-24 text-blue-400 animate-pulse" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 border-4 border-blue-400/30 rounded-full animate-ping" />
            </div>
          </div>
          <p className="text-white text-center mt-6 text-lg font-medium">
            {statusMessage}
          </p>
          <div className="mt-8 space-y-2 text-gray-400 text-sm text-center">
            <p>Tips:</p>
            <ul className="list-disc list-inside text-left">
              <li>Hold tag flat against back of phone</li>
              <li>Keep tag still for 1-2 seconds</li>
              <li>Try different positions if not working</li>
            </ul>
          </div>
        </>
      )}

      {/* Success state */}
      {scannerState === 'success' && (
        <>
          <CheckCircle2 className="h-24 w-24 text-green-500" />
          <p className="text-white text-center mt-6 text-lg font-medium">
            {statusMessage}
          </p>
          {scannedValue && (
            <p className="text-green-400 text-center mt-2 font-mono">
              {scannedValue}
            </p>
          )}
        </>
      )}

      {/* Error state */}
      {scannerState === 'error' && (
        <>
          <AlertCircle className="h-24 w-24 text-red-400" />
          <p className="text-white text-center mt-6">{statusMessage}</p>
          <div className="flex gap-2 mt-6">
            <Button variant="secondary" onClick={handleRetry}>
              Try Again
            </Button>
            {onClose && (
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            )}
          </div>
        </>
      )}

      {/* Not supported state */}
      {scannerState === 'not_supported' && (
        <>
          <Ban className="h-24 w-24 text-gray-500" />
          <p className="text-white text-center mt-6 font-medium">
            NFC Not Available
          </p>
          <p className="text-gray-400 text-center mt-2 text-sm max-w-xs">
            Web NFC only works on Chrome for Android over HTTPS. iOS Safari and
            desktop browsers do not support Web NFC.
          </p>
          <div className="flex flex-col gap-2 mt-6 w-full max-w-xs">
            {onSwitchToCamera && (
              <Button
                variant="secondary"
                onClick={onSwitchToCamera}
                className="w-full"
              >
                <Camera className="h-4 w-4 mr-2" />
                Use Camera Instead
              </Button>
            )}
            {onClose && (
              <Button variant="outline" onClick={onClose} className="w-full">
                Cancel
              </Button>
            )}
          </div>
        </>
      )}

      {/* Permission denied state */}
      {scannerState === 'permission_denied' && (
        <>
          <Ban className="h-24 w-24 text-yellow-500" />
          <p className="text-white text-center mt-6 font-medium">
            NFC Permission Required
          </p>
          <p className="text-gray-400 text-center mt-2 text-sm max-w-xs">
            {statusMessage}
          </p>
          <div className="flex flex-col gap-2 mt-6 w-full max-w-xs">
            <Button variant="secondary" onClick={handleRetry} className="w-full">
              Try Again
            </Button>
            {onSwitchToCamera && (
              <Button
                variant="outline"
                onClick={onSwitchToCamera}
                className="w-full"
              >
                <Camera className="h-4 w-4 mr-2" />
                Use Camera Instead
              </Button>
            )}
            {onClose && (
              <Button variant="ghost" onClick={onClose} className="w-full">
                Cancel
              </Button>
            )}
          </div>
        </>
      )}

      {/* Loading indicator for checking state */}
      {scannerState === 'checking' && (
        <div className="absolute bottom-4 left-0 right-0 flex justify-center">
          <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />
        </div>
      )}
    </div>
  );
}
